<!-- frontend/src/components/LCA/Stepper/LCAStepper.vue -->
<template>
  <div class="lca-stepper-container mx-auto">
    <!-- Stepper Header mit verbesserten visuellen Indikatoren -->
    <div class="stepper-header position-relative">
      <div class="progress position-absolute">
        <div class="progress-bar" role="progressbar" :style="{ width: progress + '%' }"></div>
      </div>
      
      <div class="step-indicators">
        <div 
          v-for="(step, index) in activeSteps" 
          :key="step.id"
          class="step-indicator"
          :class="{
            'completed': currentStepIndex > index,
            'active': currentStepIndex === index,
            'pending': currentStepIndex < index
          }"
        >
          <div class="step-icon">
            <i v-if="currentStepIndex > index" class="bi bi-check-lg"></i>
            <span v-else>{{ index + 1 }}</span>
          </div>
          <div class="step-label">{{ step.label }}</div>
        </div>
      </div>
    </div>

    <!-- Step Content -->
    <div class="step-content mt-4" ref="stepContent">
      <transition name="fade" mode="out-in">
        <component :is="currentComponent" :key="currentStepIndex"></component>
      </transition>
    </div>

    <!-- Navigation Buttons -->
    <div class="navigation-buttons d-flex justify-content-between align-items-center">
      <button
        class="btn nav-btn back-btn"
        @click="handleBack"
        :disabled="isLoading"
      >
        <i class="bi bi-arrow-left me-2"></i>
        <span v-if="currentStepIndex === 0">Back to Solutions</span>
        <span v-else>Back</span>
      </button>
      
      <!-- Clear Data Button - centered in navigation -->
      <button 
        class="clear-data-btn"
        @click="confirmResetProcess"
        title="Reset form and start from beginning"
        :disabled="isLoading"
      >
        <i class="bi bi-trash"></i>
        <span class="clear-btn-text">Clear All Data</span>
      </button>

      <div v-if="isLoading" class="loading-indicator">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <span class="ms-2">Processing...</span>
      </div>

      <button
        v-if="currentStepIndex < activeSteps.length - 1"
        class="btn nav-btn next-btn"
        @click="nextStep"
        :disabled="isNextDisabled || isLoading"
      >
        {{ currentStepIndex === activeSteps.length - 2 ? 'Create Environmental Impact Report' : 'Next' }}
        <i class="bi bi-arrow-right ms-2"></i>
      </button>
    </div>
    
    <!-- Reset Confirmation Modal -->
    <div v-if="showResetConfirmation" class="modal-backdrop" @click="showResetConfirmation = false">
      <div class="reset-modal" @click.stop>
        <div class="modal-header">
          <h5>Clear All Data?</h5>
        </div>
        <div class="modal-body">
          <p>This will delete all entered information and start a new form. Are you sure?</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showResetConfirmation = false">Cancel</button>
          <button class="btn btn-danger" @click="resetProcess">Clear Data</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref, nextTick, watch, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';

import { useLCAStore } from '@/stores/lca';
import { useStepperStore } from '@/stores/stepper';

// Import components and step configuration
import { 
  LifecycleStageSelector,
  SolutionSelector,
  ProviderSelector,
  TransportEmissionsCalculator,
  LCAReportViewer,
  stepConfig
} from './index';

export default {
  name: 'LCAStepper',
  components: {
    LifecycleStageSelector,
    SolutionSelector,
    ProviderSelector,
    TransportEmissionsCalculator,
    LCAReportViewer,
  },
  setup() {
    const stepperStore = useStepperStore();
    const lcaStore = useLCAStore();
    const router = useRouter();
    const route = useRoute();

    const isLoading = ref(false);
    const wrapperEl = ref(null);
    const primaryColor = '#87b140';
    const showResetConfirmation = ref(false);

    // Use the step configuration
    const activeSteps = computed(() => stepConfig.getActiveSteps());

    // Current step tracking
    const currentStepIndex = ref(0);
    const currentComponent = computed(() => {
      if (currentStepIndex.value >= 0 && currentStepIndex.value < activeSteps.value.length) {
        return activeSteps.value[currentStepIndex.value].component;
      }
      return null;
    });

    // "isNextDisabled" => using validation functions from step config
    const isNextDisabled = computed(() => {
      if (currentStepIndex.value >= 0 && currentStepIndex.value < activeSteps.value.length) {
        const validateFn = activeSteps.value[currentStepIndex.value].validateFn;
        return !validateFn(lcaStore.lcaData);
      }
      return true;
    });

    onMounted(async () => {
      // Fresh-session guard: when entering at first LCA step, clear persisted LCA state
      try {
        if (route?.name === 'lca-solution') {
          lcaStore.resetStore();
        }
      } catch (e) {
        console.warn('LCA store reset skipped:', e);
      }

      await fetchSolutions();
      updateCurrentStepFromRoute();
    });

    watch(() => route.name, () => {
      updateCurrentStepFromRoute();
    });

    function updateCurrentStepFromRoute() {
      const currentRouteName = route.name;
      const idx = activeSteps.value.findIndex((s) => `lca-${s.path}` === currentRouteName);
      if (idx !== -1) {
        currentStepIndex.value = idx;
      }
    }

    async function fetchSolutions() {
      isLoading.value = true;
      try {
        if (stepperStore.solutions && stepperStore.solutions.length > 0) {
          lcaStore.setSolutions(stepperStore.solutions);
        } else {
          const userInputs = {
            companyProfile: { ...stepperStore.companyProfile },
            wasteInfo: { ...stepperStore.wasteInfo },
          };
          const includeVCG = true;
          const resp = await axios.post(`/solutions/match?includeVCG=${includeVCG}`, {
            userInputs
          });
          const data = resp.data;
          if (data.solutions) {
            stepperStore.setSolutions(data.solutions);
            lcaStore.setSolutions(data.solutions);
          } else {
            stepperStore.setSolutions([]);
            lcaStore.setSolutions([]);
          }
        }
      } catch (error) {
        console.error('Error matching solutions:', error);
        stepperStore.setSolutions([]);
        lcaStore.setSolutions([]);
      } finally {
        isLoading.value = false;
      }
    }

    async function nextStep() {
      isLoading.value = true;
      if (currentStepIndex.value < activeSteps.value.length - 1) {
        // Special handling for provider loading when moving from solution to provider step
        if (activeSteps.value[currentStepIndex.value].id === 'solution' && 
            activeSteps.value[currentStepIndex.value + 1].id === 'provider') {
          const selSolution = lcaStore.lcaData.selectedSolution;
          if (selSolution && selSolution.solution_id) {
            await lcaStore.fetchProviderCompanies(selSolution.solution_id);
          }
        }
        
        // Navigate to next step
        const nextPath = activeSteps.value[currentStepIndex.value + 1].path;
        router.push({ name: `lca-${nextPath}` });
        scrollToTop();
      }
      isLoading.value = false;
    }

    function prevStep() {
      if (currentStepIndex.value > 0) {
        const prevPath = activeSteps.value[currentStepIndex.value - 1].path;
        router.push({ name: `lca-${prevPath}` });
        scrollToTop();
      }
    }

    function handleBack() {
      if (currentStepIndex.value === 0) {
        // Back from first LCA step - go to the original solutions overview
        router.push({ name: 'solutions' });
      } else {
        // Otherwise, just go back one step within the LCA process
        prevStep();
      }
    }

    function scrollToTop() {
      nextTick(() => {
        if (wrapperEl.value) {
          const offsetTop =
            wrapperEl.value.getBoundingClientRect().top + window.pageYOffset - 100;
          window.scrollTo({
            top: offsetTop,
            behavior: 'smooth',
          });
        }
      });
    }

    const progress = computed(() => {
      return (currentStepIndex.value / (activeSteps.value.length - 1)) * 100;
    });
    
    // Reset functionality
    function confirmResetProcess() {
      // Check if there's any data to reset
      const hasCompanyData = stepperStore.companyProfile.name || 
                             stepperStore.companyProfile.location.address;
      const hasWasteData = stepperStore.wasteInfo.surplusName || 
                          stepperStore.wasteInfo.foodGroups.length > 0;
      const hasSolutions = stepperStore.solutions.length > 0;
      const hasLCAData = lcaStore.lcaData.selectedSolution || 
                        lcaStore.lcaData.selectedProvider;
      
      if (hasCompanyData || hasWasteData || hasSolutions || hasLCAData) {
        showResetConfirmation.value = true;
      } else {
        // If no data, just reset without confirmation
        resetProcess();
      }
    }
    
    // Reset the entire process and start from beginning
    async function resetProcess() {
      showResetConfirmation.value = false;
      
      // Reset all stores
      stepperStore.resetStore();
      lcaStore.resetStore();
      
      // Navigate to first step and force reload
      await router.push({ name: 'company-data' });
      
      // Force reload to ensure all components re-render with fresh data
      window.location.reload();
      
      console.log('Process has been reset from LCA');
    }

    return {
      wrapperEl,
      currentStepIndex,
      currentComponent,
      activeSteps,
      isLoading,
      primaryColor,
      stepperStore,
      lcaStore,
      fetchSolutions,
      nextStep,
      prevStep,
      handleBack,
      scrollToTop,
      progress,
      route,
      router,
      isNextDisabled,
      showResetConfirmation,
      confirmResetProcess,
      resetProcess,
    };
  },
};
</script>

<style scoped>
.lca-stepper-container {
  max-width: 900px;
  padding: 2.5rem 2rem;
  margin: 1.5rem auto;
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08), 0 5px 15px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(135, 177, 64, 0.1);
  position: relative;
  overflow: hidden;
}

.lca-stepper-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background-color: #87B140;
}

/* Stepper Header Styles */
.stepper-header {
  position: relative;
  margin-bottom: 2.5rem;
  padding: 0 1.5rem;
}

.progress {
  height: 4px;
  width: 100%;
  background-color: #eaeaea;
  border-radius: 4px;
  top: 30px;
  z-index: 1;
  overflow: visible;
}

.progress-bar {
  background-color: #87B140;
  transition: width 0.5s ease;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.step-indicators {
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 2;
}

.step-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 33.333%;
  text-align: center;
  transition: transform 0.3s ease;
}

.step-indicator.active {
  transform: translateY(-5px);
}

.step-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  font-weight: 600;
  font-size: 1.25rem;
  border: 2px solid #e0e0e0;
  color: #555;
  transition: all 0.3s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.step-indicator.active .step-icon {
  background-color: #f2f5e7;
  border-color: #87B140;
  color: #558000;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: scale(1.1);
}

.step-indicator.completed .step-icon {
  background-color: #87B140;
  border-color: #87B140;
  color: #ffffff;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
}

.step-indicator.pending .step-icon {
  background-color: #ffffff;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.step-label {
  color: #555;
  font-weight: 600;
  margin-top: 8px;
  transition: color 0.3s ease;
  font-size: 1rem;
}

.step-indicator.active .step-label {
  color: #333;
  font-weight: 700;
}

.step-indicator.completed .step-label {
  color: #333;
}

/* Step Content */
.step-content {
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  margin-bottom: 1.5rem;
}

/* Navigation Buttons */
.navigation-buttons {
  margin-top: 2rem;
  padding: 0.5rem 0;
}

.nav-btn {
  padding: 0.875rem 1.75rem;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
  letter-spacing: 0.02em;
}

.back-btn {
  border: none;
  background-color: #f0f0f0;
  color: #444;
}

.back-btn:hover:not(:disabled) {
  background-color: #e5e5e5;
  color: #222;
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
}

.next-btn {
  background-color: #87B140;
  border: none;
  color: white;
}

.next-btn:hover:not(:disabled) {
  background-color: #75a236;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.nav-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05) !important;
}

/* Loading Indicator */
.loading-indicator {
  display: flex;
  align-items: center;
  color: #558000;
  font-weight: 600;
  background-color: #f2f5e7;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(135, 177, 64, 0.15);
  border: 1px solid rgba(135, 177, 64, 0.2);
}

.spinner-border {
  color: #87B140;
  width: 1.5rem;
  height: 1.5rem;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .lca-stepper-container {
    padding: 1.5rem 1rem;
    margin: 1rem 0.75rem;
    border-radius: 12px;
  }
  
  .step-icon {
    width: 50px;
    height: 50px;
    font-size: 1rem;
  }
  
  .step-label {
    font-size: 0.9rem;
  }
  
  .navigation-buttons {
    margin-top: 1.5rem;
  }
  
  .nav-btn {
    padding: 0.75rem 1.25rem;
    font-size: 0.95rem;
  }
}

@media (max-width: 576px) {
  .lca-stepper-container {
    padding: 1.25rem 0.75rem;
    margin: 0.75rem 0.5rem;
  }
  
  .stepper-header {
    padding: 0 0.5rem;
    margin-bottom: 2rem;
  }
  
  .step-icon {
    width: 40px;
    height: 40px;
    font-size: 0.9rem;
  }
  
  .step-label {
    font-size: 0.8rem;
  }
  
  .navigation-buttons {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .back-btn, .next-btn {
    width: 100%;
    justify-content: center;
  }
  
  .loading-indicator {
    width: 100%;
    justify-content: center;
    margin: 0.5rem 0;
  }
}

/* Clear Data Button in Navigation */
.clear-data-btn {
  background: transparent;
  border: none;
  color: #dc3545;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  opacity: 0.8;
}

.clear-data-btn::before,
.clear-data-btn::after {
  content: '';
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 24px;
  background: #e0e0e0;
}

.clear-data-btn::before {
  left: -20px;
}

.clear-data-btn::after {
  right: -20px;
}

.clear-data-btn:hover:not(:disabled) {
  background: rgba(220, 53, 69, 0.1);
  color: #a02633;
  opacity: 1;
  border-radius: 6px;
}

.clear-data-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.clear-data-btn i {
  font-size: 0.95rem;
}

.clear-btn-text {
  display: inline;
}

/* Modal Styles */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(3px);
}

.reset-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  max-width: 450px;
  width: 90%;
  margin: 20px;
  animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.reset-modal .modal-header {
  padding: 20px 24px 10px;
  border-bottom: 1px solid #e0e0e0;
}

.reset-modal .modal-header h5 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

.reset-modal .modal-body {
  padding: 20px 24px;
}

.reset-modal .modal-body p {
  margin: 0;
  color: #555;
  line-height: 1.6;
  font-size: 1rem;
}

.reset-modal .modal-footer {
  padding: 15px 24px 20px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  border-top: 1px solid #e0e0e0;
}

.reset-modal .btn {
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.reset-modal .btn-secondary {
  background: #f8f9fa;
  color: #6c757d;
}

.reset-modal .btn-secondary:hover {
  background: #e9ecef;
  color: #495057;
}

.reset-modal .btn-danger {
  background: #dc3545;
  color: white;
}

.reset-modal .btn-danger:hover {
  background: #c82333;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Mobile styles */
@media (max-width: 576px) {
  .clear-btn-text {
    display: none;
  }
  
  .clear-data-btn {
    padding: 0.4rem;
    font-size: 1.1rem;
  }
  
  .clear-data-btn::before,
  .clear-data-btn::after {
    display: none;
  }
  
  .clear-data-btn i {
    margin: 0;
  }
}
</style>
